import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Zap } from "lucide-react";
import { ProductCard } from "./ProductCard";

const MAX_ITEMS = 3;
// A product is considered "NEW" if added in the last 60 days
const NEW_WINDOW_DAYS = 60;

export const JustDropped = ({ products, wishlistIds, onWishlistChange }) => {
  const { t } = useTranslation();

  const fresh = useMemo(() => {
    const now = Date.now();
    const cutoff = now - NEW_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return [...products]
      .filter((p) => p.created_at && new Date(p.created_at).getTime() >= cutoff)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, MAX_ITEMS);
  }, [products]);

  // Hide the entire section when there's nothing new — avoids an empty slot on the home
  if (fresh.length === 0) return null;

  return (
    <section
      id="just-dropped"
      data-testid="just-dropped-section"
      className="relative bg-zinc-950 py-24 overflow-hidden"
    >
      {/* decorative diagonal stripes top + bottom */}
      <div className="absolute top-0 left-0 right-0 h-2 diagonal-stripes" />
      <div className="absolute bottom-0 left-0 right-0 h-2 diagonal-stripes" />

      {/* japanese vertical mark on the side */}
      <div className="hidden lg:block absolute right-6 top-1/2 -translate-y-1/2 font-jp text-yellow-400/10 text-[180px] leading-none select-none pointer-events-none">
        新作
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-red-500 text-black p-2.5 brutal-shadow-black">
            <Zap className="w-5 h-5" strokeWidth={3} />
          </div>
          <div className="font-jp text-red-400 text-sm tracking-widest">{t("just_dropped.eyebrow")}</div>
        </div>

        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <h2 className="font-heading text-5xl sm:text-6xl uppercase text-white leading-none">
            {t("just_dropped.title_1")} <span className="text-yellow-400">{t("just_dropped.title_accent")}</span>
          </h2>
          <p className="text-zinc-400 max-w-md text-base">
            <Sparkles className="inline w-4 h-4 text-yellow-400 mr-1 -mt-1" strokeWidth={3} />
            {t("just_dropped.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {fresh.map((p) => (
            <div key={p.id} className="relative">
              {/* NEW corner badge */}
              <div
                data-testid={`new-badge-${p.id}`}
                className="absolute -top-2 -right-2 z-20 bg-red-500 text-black font-heading uppercase text-[11px] tracking-widest px-3 py-1.5 border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,0.9)] -rotate-3"
              >
                {t("just_dropped.badge")}
              </div>
              <ProductCard product={p} wishlistIds={wishlistIds} onWishlistChange={onWishlistChange} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JustDropped;
